import {useCallback} from "react";
import {
    DragStartEvent,
    DragEndEvent,
    pointerWithin,
    closestCenter,
} from "@dnd-kit/core";
import {Transaction, Document, LIST_IDS} from "../dndTypes.ts";
import {TemporaryAttachment} from "../dndReducer.ts";
import {RenderTypeEnum, isBillDocument, isPartialMatch} from "../types.ts";
import {canMatch, canMatchBillDirections} from "../utils/utils.ts";

interface UseDragAndDropProps {
    transactions: Transaction[];
    documents: Document[];
    temporaryAttachments: TemporaryAttachment[];
    onAttachDocument: (hostId: string, documentId: string) => void;
    onAttachTransaction: (hostId: string, transactionId: string) => void;
    onDetach: (hostId: string, guestId: string) => void;
    setActiveId: (id: string | null) => void;
}

export function useDragAndDrop({
    transactions,
    documents,
    temporaryAttachments,
    onAttachDocument,
    onAttachTransaction,
    onDetach,
    setActiveId,
}: UseDragAndDropProps) {
    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    }, [setActiveId]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const {active, over} = event;
        setActiveId(null);

        if (!over) return;

        const activeId = active.id as string;
        const overId = over.id as string;

        const billDocuments = documents.filter(isBillDocument);
        const partialMatches = documents.filter(isPartialMatch);
        const billDirectionById = new Map(
            billDocuments.map((bill) => [bill.document_id, bill.document_direction]),
        );
        const attachedBillDirections = (hostId: string) => (
            temporaryAttachments
                .filter((attachment): attachment is Extract<TemporaryAttachment, { kind: 'document' }> => (
                    attachment.host_id === hostId && attachment.kind === 'document'
                ))
                .map((attachment) => billDirectionById.get(attachment.document_id))
        );

        const attachedDoc = temporaryAttachments.find(
            a => a.kind === 'document' && a.document_id === activeId
        );
        const attachedTx = temporaryAttachments.find(
            a => a.kind === 'transaction' && a.transaction_id === activeId
        );

        if (attachedDoc && overId === LIST_IDS.DOCUMENTS) {
            onDetach(attachedDoc.host_id, activeId);
            return;
        }

        if (attachedTx && overId === LIST_IDS.TRANSACTIONS) {
            onDetach(attachedTx.host_id, activeId);
            return;
        }

        const overGroup = partialMatches.find((d) => d.match_id === overId);
        if (overGroup) {
            const groupNrsRemainder = overGroup.nrs_remainder ?? null;
            const groupCurrency = overGroup.remainder.currency ?? undefined;

            const draggedTx = transactions.find((t) => t.transaction_id === activeId);
            if (draggedTx) {
                const canBeMatched = canMatch(
                    groupCurrency,
                    draggedTx.total_amount_payable.currency ?? undefined,
                    groupNrsRemainder,
                    draggedTx.nrs_amount ?? null,
                );
                if (canBeMatched) {
                    onAttachTransaction(overGroup.match_id, draggedTx.transaction_id);
                }
                return;
            }

            const draggedDoc = billDocuments.find((d) => d.document_id === activeId);
            if (draggedDoc) {
                const canBeMatched = canMatch(
                    groupCurrency,
                    draggedDoc.scanned_fields?.total_amount_payable?.currency ?? undefined,
                    groupNrsRemainder,
                    draggedDoc.nrs_amount ?? null,
                );
                const directionsCompatible = canMatchBillDirections(
                    [
                        ...overGroup.bills.map((bill) => bill.document_direction),
                        ...attachedBillDirections(overGroup.match_id),
                    ],
                    draggedDoc.document_direction,
                );
                if (canBeMatched && directionsCompatible) {
                    onAttachDocument(overGroup.match_id, draggedDoc.document_id);
                }
            }
            return;
        }

        const activeIsTransaction = transactions.some((t) => t.transaction_id === activeId);
        const activeIsDocument = billDocuments.some((d) => d.document_id === activeId);
        const overIsTransaction = transactions.some((t) => t.transaction_id === overId);
        const overIsDocument = billDocuments.some((d) => d.document_id === overId);

        if (activeIsDocument && overIsTransaction) {
            const doc = billDocuments.find((d) => d.document_id === activeId);
            const tx = transactions.find((t) => t.transaction_id === overId);
            if (doc && tx) {
                const txIsMatched = tx.render_type === RenderTypeEnum.TRANSACTION_MATCH_PART;
                if (txIsMatched) {
                    return;
                }
                const canBeMatched = canMatch(
                    tx.total_amount_payable.currency ?? undefined,
                    doc.scanned_fields?.total_amount_payable?.currency ?? undefined,
                    tx.nrs_amount ?? null,
                    doc.nrs_amount ?? null,
                );
                const directionsCompatible = canMatchBillDirections(
                    attachedBillDirections(tx.transaction_id),
                    doc.document_direction,
                );
                if (!canBeMatched || !directionsCompatible) {
                    return;
                }
                onAttachDocument(tx.transaction_id, doc.document_id);
            }
            return;
        }

        if (activeIsTransaction && overIsDocument) {
            const tx = transactions.find((t) => t.transaction_id === activeId);
            const doc = billDocuments.find((d) => d.document_id === overId);
            if (tx && doc) {
                const docIsMatched = doc.render_type === RenderTypeEnum.BILL_MATCH_PART;
                if (docIsMatched) {
                    return;
                }
                const canBeMatched = canMatch(
                    tx.total_amount_payable.currency ?? undefined,
                    doc.scanned_fields?.total_amount_payable?.currency ?? undefined,
                    doc.nrs_amount ?? null,
                    tx.nrs_amount ?? null,
                );
                if (!canBeMatched) {
                    return;
                }
                onAttachTransaction(doc.document_id, tx.transaction_id);
            }
            return;
        }

        if (activeIsDocument && overIsDocument) {
            const activeDoc = billDocuments.find((d) => d.document_id === activeId);
            const overDoc = billDocuments.find((d) => d.document_id === overId);
            if (activeDoc && overDoc && activeId !== overId) {
                const overIsMatched = overDoc.render_type === RenderTypeEnum.BILL_MATCH_PART;
                const activeIsMatched = activeDoc.render_type === RenderTypeEnum.BILL_MATCH_PART;
                if (overIsMatched || activeIsMatched) {
                    return;
                }
                const canBeMatched = canMatch(
                    overDoc.scanned_fields?.total_amount_payable?.currency ?? undefined,
                    activeDoc.scanned_fields?.total_amount_payable?.currency ?? undefined,
                    overDoc.nrs_amount ?? null,
                    activeDoc.nrs_amount ?? null,
                );
                const directionsCompatible = canMatchBillDirections(
                    [overDoc.document_direction, ...attachedBillDirections(overDoc.document_id)],
                    activeDoc.document_direction,
                );
                if (!canBeMatched || !directionsCompatible) {
                    return;
                }
                onAttachDocument(overDoc.document_id, activeDoc.document_id);
            }
            return;
        }

        if (activeIsTransaction && overIsTransaction) {
            const activeTx = transactions.find((t) => t.transaction_id === activeId);
            const overTx = transactions.find((t) => t.transaction_id === overId);
            if (activeTx && overTx && activeId !== overId) {
                const overIsMatched = overTx.render_type === RenderTypeEnum.TRANSACTION_MATCH_PART;
                const activeIsMatched = activeTx.render_type === RenderTypeEnum.TRANSACTION_MATCH_PART;
                if (overIsMatched || activeIsMatched) {
                    return;
                }
                const canBeMatched = canMatch(
                    overTx.total_amount_payable.currency ?? undefined,
                    activeTx.total_amount_payable.currency ?? undefined,
                    overTx.nrs_amount ?? null,
                    activeTx.nrs_amount ?? null,
                );
                if (!canBeMatched) {
                    return;
                }
                onAttachTransaction(overTx.transaction_id, activeTx.transaction_id);
            }
        }
    }, [
        transactions,
        documents,
        temporaryAttachments,
        onAttachDocument,
        onAttachTransaction,
        onDetach,
        setActiveId,
    ]);

    const collisionDetection = useCallback((args: Parameters<typeof pointerWithin>[0]) => {
        const {active} = args;
        const activeId = active.id as string;

        const isAttachedItem = temporaryAttachments.some(
            a => (a.kind === 'document' ? a.document_id : a.transaction_id) === activeId
        );

        if (isAttachedItem) {
            const pointerCollisions = pointerWithin(args);
            const listCollisions = pointerCollisions.filter(
                (collision) =>
                    collision.id === LIST_IDS.TRANSACTIONS ||
                    collision.id === LIST_IDS.DOCUMENTS
            );
            if (listCollisions.length > 0) {
                return listCollisions;
            }
            if (pointerCollisions.length > 0) {
                return pointerCollisions;
            }
        }

        const pointerCollisions = pointerWithin(args);

        const activeIsTransaction = transactions.some((t) => t.transaction_id === activeId);
        const activeIsDocument = documents.some((d) => isBillDocument(d) && d.document_id === activeId);

        if ((activeIsTransaction || activeIsDocument) && pointerCollisions.length > 0) {
            const parentCardCollisions = pointerCollisions.filter((collision) => {
                const isParentCard = transactions.some((t) => t.transaction_id === collision.id) ||
                                    documents.some((d) => isBillDocument(d) && d.document_id === collision.id) ||
                                    documents.some((d) => isPartialMatch(d) && d.match_id === collision.id);
                return isParentCard;
            });

            if (parentCardCollisions.length > 0) {
                return parentCardCollisions;
            }
        }

        if (pointerCollisions.length > 0) {
            return pointerCollisions;
        }
        return closestCenter(args);
    }, [temporaryAttachments, transactions, documents]);

    return {
        handleDragStart,
        handleDragEnd,
        collisionDetection,
    };
}
